"use client";

import { useEffect, useState, type MouseEvent } from "react";
import { Dialog, DialogBackdrop, DialogPanel } from "@headlessui/react";
import { Menu, X } from "lucide-react";
import { usePathname } from "next/navigation";
import Link from "next/link";
import Image from "next/image";
import { cn } from "@/components/ui/cn";
import { langPath, type Lang } from "@/lib/i18n";
import { useDict } from "@/components/i18n/DictProvider";
import { PageView } from "@/components/analytics/PageView";

const COLLAPSE_COOKIE = "lh.navCollapsed";

type Props = {
  lang: Lang;
  topBar: React.ReactNode;
  /** Portal display name — sidebar wordmark, collapsed initial, aria label. */
  brandName: string;
  /** White-on-dark mark for the rail; blank → the wordmark. */
  logo: string;
  leftNav: React.ReactNode;
  children: React.ReactNode;
  /** Server-read from the cookie so SSR renders the correct sidebar width —
   *  no expand→collapse flip (or width animation) on load. */
  initialCollapsed: boolean;
};

/**
 * Holds the open/closed state for the desktop collapse toggle and the
 * mobile drawer. Rendered around the app shell so the topbar buttons and
 * the nav itself share state without prop-drilling through server components.
 */
export function AppShellClient({
  lang,
  topBar,
  leftNav,
  children,
  initialCollapsed,
  brandName,
  logo,
}: Props) {
  const t = useDict();
  const [collapsed, setCollapsed] = useState(initialCollapsed);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const pathname = usePathname();
  const [previousPath, setPreviousPath] = useState(pathname);
  // The app layout persists between routes, including browser back/forward.
  if (pathname !== previousPath) {
    setPreviousPath(pathname);
    setDrawerOpen(false);
  }

  useEffect(() => {
    // Derive md from the theme token rather than restating it: the drawer is
    // hidden by the `md:hidden` CLASS, so a themed --breakpoint-md would leave a
    // hardcoded query behind — an "open" dialog on desktop holding the scroll
    // lock and a focus trap on an invisible panel. Falls back to Tailwind's own
    // default when the var is absent, which is what the literal used to be.
    const token = getComputedStyle(document.documentElement)
      .getPropertyValue("--breakpoint-md")
      .trim();
    const desktop = window.matchMedia(`(min-width: ${token || "48rem"})`);
    const closeOnDesktop = () => {
      if (desktop.matches) setDrawerOpen(false);
    };
    desktop.addEventListener("change", closeOnDesktop);
    return () => desktop.removeEventListener("change", closeOnDesktop);
  }, []);

  // Bubble phase, NOT capture: `defaultPrevented` only reports handlers that
  // already ran, and next/link's (and any nav item's) onClick is bubble — in
  // capture we would close the drawer for a click the link goes on to cancel.
  // Real navigations are already covered by the pathname check above; this only
  // has to catch same-pathname query/hash links.
  function closeOnNavigation(event: MouseEvent<HTMLDivElement>) {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const anchor = (event.target as Element).closest<HTMLAnchorElement>("a[href]");
    if (
      anchor &&
      !anchor.hasAttribute("download") &&
      (!anchor.target || anchor.target === "_self")
    ) {
      setDrawerOpen(false);
    }
  }

  function persist(next: boolean) {
    setCollapsed(next);
    // Cookie (not localStorage) so the server reads it and renders the correct
    // width on the next request — no flip on load.
    try {
      document.cookie = `${COLLAPSE_COOKIE}=${next ? "1" : "0"};path=/;max-age=31536000;samesite=lax`;
    } catch {
      // ignore
    }
  }

  // Admin-style shell: a full-height sidebar on the left (brand at its top),
  // and a content column whose sticky topbar spans the content only — not the
  // sidebar. The outer flex is a ROW; the topbar lives inside the right column.
  return (
    <div
      data-collapsed={collapsed ? "1" : "0"}
      data-drawer={drawerOpen ? "1" : "0"}
      className="group min-h-screen flex"
    >
      <PageView />

      {/* Full-height sidebar — brand header + nav, top to bottom. */}
      <aside
        aria-label={t.appNav.appNavigation}
        className={cn(
          "hidden md:flex md:sticky md:top-0 md:self-start md:h-screen md:overflow-y-auto md:overflow-x-hidden border-r border-border flex-col transition-[width] duration-200",
          collapsed ? "md:w-[72px]" : "md:w-64",
        )}
        style={{
          background: "var(--color-app-nav)",
        }}
      >
        {/* Brand — the PORTAL's white-on-dark mark, resolved server-side; blank
            renders the name as a wordmark. Collapses to its first letter.
            NB: the mark must be white-on-dark; a light-primary fork must supply
            a suitable one (see the --color-app-nav note in globals.css). */}
        <div className="h-14 shrink-0 flex items-center px-4 border-b border-app-nav-foreground/10 group-data-[collapsed=1]:px-0 group-data-[collapsed=1]:justify-center">
          <Link
            href={langPath(lang, "/dashboard")}
            aria-label={brandName}
            className="inline-flex items-center transition-opacity hover:opacity-80"
          >
            {logo ? (
              <Image
                src={logo}
                alt={brandName}
                width={443}
                height={85}
                className="h-5 w-auto group-data-[collapsed=1]:hidden"
              />
            ) : (
              <span className="font-display font-extrabold tracking-tight text-app-nav-foreground group-data-[collapsed=1]:hidden">
                {brandName}
              </span>
            )}
            <span className="hidden group-data-[collapsed=1]:grid place-items-center size-9 rounded-lg bg-accent/20 text-accent font-display font-extrabold">
              {brandName.trim().charAt(0).toUpperCase()}
            </span>
          </Link>
        </div>
        {leftNav}
      </aside>

      <Dialog
        id="app-mobile-navigation"
        open={drawerOpen}
        onClose={setDrawerOpen}
        aria-label={t.appNav.appNavigation}
        className="relative z-50 md:hidden"
      >
        <DialogBackdrop className="fixed inset-0 bg-black/40" />
        <DialogPanel
          data-collapsed="0"
          onClick={closeOnNavigation}
          className="group fixed inset-y-0 left-0 flex w-72 max-w-[calc(100%-3rem)] flex-col overflow-y-auto overscroll-contain border-r border-border pb-[env(safe-area-inset-bottom)]"
          style={{ background: "var(--color-app-nav)", boxShadow: "var(--shadow-lift)" }}
        >
          <div className="flex min-h-14 shrink-0 items-center gap-2 border-b border-app-nav-foreground/10 pl-4 pr-2 pt-[env(safe-area-inset-top)]">
            <Link
              href={langPath(lang, "/dashboard")}
              className="min-w-0 flex-1 truncate font-display text-[15px] font-extrabold text-app-nav-foreground"
            >
              {brandName}
            </Link>
            <button
              type="button"
              data-autofocus
              onClick={() => setDrawerOpen(false)}
              aria-label={t.nav.closeSidebar}
              className="inline-flex size-11 shrink-0 items-center justify-center rounded-lg text-app-nav-foreground hover:bg-app-nav-foreground/10"
            >
              <X className="size-[22px]" aria-hidden strokeWidth={2} />
            </button>
          </div>
          {/* Second mount of the same nav. The aside above is `display:none`
              below md (so assistive tech sees ONE landmark, not two) and this
              panel is unmounted whenever the drawer is closed, so the duplicate
              is transient and mobile-only. Rendering it once would mean
              choosing the surface from the viewport, which the server cannot
              know — that is a hydration mismatch, a worse bug than this. */}
          {leftNav}
        </DialogPanel>
      </Dialog>

      {/* Content column — sticky topbar over the content, then the page. */}
      {/* `app-ground` (globals.css): the one ground every signed-in page sits on. */}
      <div className="app-ground flex-1 min-w-0 flex flex-col">
        <header
          className="sticky top-0 z-30 border-b border-border h-14 flex items-center px-3 md:px-4 gap-2"
          style={{ background: "var(--color-app-sidebar)" }}
        >
          <button
            type="button"
            onClick={() => setDrawerOpen((v) => !v)}
            aria-label={drawerOpen ? t.nav.closeSidebar : t.nav.openSidebar}
            aria-expanded={drawerOpen}
            // No aria-controls: HeadlessUI's Dialog wraps itself in a Transition
            // whose `unmount` defaults to true, so nothing carries that id while
            // the drawer is closed — exactly the state the attribute described.
            aria-haspopup="dialog"
            className="md:hidden w-10 h-10 inline-flex items-center justify-center rounded-lg text-foreground hover:bg-muted"
          >
            <Menu className="size-[22px]" aria-hidden strokeWidth={2} />
          </button>
          <button
            type="button"
            onClick={() => persist(!collapsed)}
            aria-label={collapsed ? t.nav.expand : t.nav.collapse}
            title={collapsed ? t.nav.expand : t.nav.collapse}
            className="hidden md:inline-flex w-10 h-10 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            {/* Panel/sidebar collapse glyph */}
            <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
              <rect
                x="3"
                y="4"
                width="18"
                height="16"
                rx="3"
                stroke="currentColor"
                strokeWidth="2"
                fill="none"
              />
              <line x1="9.5" y1="4" x2="9.5" y2="20" stroke="currentColor" strokeWidth="2" />
              {collapsed ? (
                <path
                  d="M14 9l3 3-3 3"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  fill="none"
                />
              ) : (
                <path
                  d="M17 9l-3 3 3 3"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  fill="none"
                />
              )}
            </svg>
          </button>

          {/* Mobile brand. The sidebar carries the wordmark on desktop, but it
              is hidden below md — without this the portal is unnamed on phones,
              and there is nothing to tap to get home. The rail's mark is
              white-on-dark, so this surface renders the NAME, never that asset. */}
          <Link
            href={langPath(lang, "/dashboard")}
            className="font-display truncate text-[15px] font-extrabold tracking-tight text-foreground md:hidden"
          >
            {brandName}
          </Link>

          {topBar}
        </header>

        <main id="main" className="flex-1 min-w-0">
          {children}
        </main>
      </div>
    </div>
  );
}
